import { useState, useEffect, useMemo, useCallback } from 'react';
import { integrationsServiceV2 } from '../services/integrationsServiceV2';
import { toast } from 'sonner';

/**
 * Custom hook for V2 integration management.
 * Encapsulates all state and logic for fetching, connecting, and managing integrations.
 */
export const useIntegrationsV2 = () => {
  const [state, setState] = useState({
    definitions: [],
    connections: [],
    isLoading: true,
    error: null,
  });

  const fetchData = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError) throw new Error(accountIdError);

      const [definitions, connections] = await Promise.all([
        integrationsServiceV2.getIntegrationDefinitions(),
        integrationsServiceV2.getIntegrationConnections(accountId)
      ]);

      setState(prev => ({ ...prev, definitions, connections, isLoading: false }));
    } catch (error) {
      setState(prev => ({ ...prev, error: error.message, isLoading: false }));
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const connectIntegration = useCallback(async (integrationId, credentials) => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      // NOTE: This account ID is a placeholder and should be replaced with the
      // actual ID of the currently authenticated user.
      const accountId = 'ed0bfb53-0978-44c9-af3f-0afdcd72ca21'; 

      await integrationsServiceV2.connectIntegration(integrationId, accountId, credentials);

      toast.success('Integration connected successfully!');
      await fetchData(); // Refresh data to show the new connection status
    } catch (error) {
      console.error('Connection process failed:', error);
      toast.error(error.message || 'Failed to connect integration.');
      setState(prev => ({ ...prev, error: error.message }));
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [fetchData]);

  const disconnectIntegration = useCallback(async (connectionId) => {
    try {
      await integrationsServiceV2.deleteConnection(connectionId);
      toast.success('Integration disconnected.');
      await fetchData();
    } catch (error) {
      toast.error(`Failed to disconnect: ${error.message}`);
      throw error;
    }
  }, [fetchData]);

  const viewModel = useMemo(() => {
    return state.definitions.map(def => {
      const conn = state.connections.find(c => c.integration_id === def.id);

      let status = 'NOT_CONNECTED';
      if (conn) {
        switch (conn.status) {
          case 'active':
            status = 'CONNECTED';
            break;
          case 'failed':
            status = 'ERROR';
            break;
          case 'disabled':
            status = 'DISABLED';
            break;
          case 'pending':
          default:
            status = 'NOT_CONNECTED';
            break;
        }
      }

      return {
        ...def,
        usesV2Backend: true,
        connection: conn,
        status: status,
      };
    });
  }, [state.definitions, state.connections]);

  return {
    integrationsV2: viewModel,
    isLoadingV2: state.isLoading,
    errorV2: state.error,
    connectIntegrationV2: connectIntegration,
    disconnectIntegrationV2: disconnectIntegration,
    refreshIntegrationsV2: fetchData,
  };
}; 